import React from 'react';
import { X, Loader2, CheckCircle2, AlertCircle, Music, XCircle } from 'lucide-react';
import { Badge, Button } from '@components/ui';
import { withUploadsToken } from '@/utils/uploadsAuth';
import type { GenerationResult } from '@/hooks/useSunoIntegration';
import type { SunoClip } from '@/types';

interface GenerationProgressProps {
  generation: GenerationResult | null;
  onDismiss: () => void;
  onCancel?: () => void;
}

const statusColors: Record<string, string> = {
  submitted: 'text-slate-400',
  queued: 'text-slate-400',
  polling: 'text-blue-400',
  streaming: 'text-green-400',
  complete: 'text-emerald-400',
  error: 'text-red-400',
};

function ClipCard({ clip, label }: { clip: SunoClip; label: string }) {
  const status = clip.status || 'submitted';
  const isStreaming = status === 'streaming';
  const isComplete = status === 'complete';
  const hasAudio = !!clip.audio_url;

  return (
    <div className="flex-1 min-w-0 bg-slate-900/80 border border-slate-700/50 rounded-lg p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</span>
        <Badge
          variant={isComplete ? 'success-muted' : isStreaming ? 'info-muted' : status === 'error' ? 'danger-muted' : 'default'}
          size="xs"
        >
          {status}
        </Badge>
      </div>

      {/* Album art */}
      {clip.imageUrl ? (
        <img src={withUploadsToken(clip.imageUrl)} alt={clip.title} className="w-full aspect-square rounded-md object-cover" />
      ) : (
        <div className="w-full aspect-square rounded-md bg-slate-800 flex items-center justify-center">
          <Music size={24} className="text-slate-600" />
        </div>
      )}

      <p className="text-xs font-medium text-slate-300 truncate" title={clip.title}>{clip.title}</p>

      {/* Audio player — appears during streaming (live preview) or complete */}
      {hasAudio && (isStreaming || isComplete) && (
        <div className="space-y-1">
          {isStreaming && (
            <div className="flex items-center gap-1.5">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
              </span>
              <span className="text-[9px] font-bold uppercase tracking-widest text-green-400">Live Preview</span>
            </div>
          )}
          <audio
            key={clip.audio_url}
            controls
            preload="none"
            className="w-full h-8 [&::-webkit-media-controls-panel]:bg-slate-800"
          >
            <source src={clip.audio_url} />
          </audio>
        </div>
      )}

      {/* Video status badge */}
      {clip.video_status && (
        <Badge
          variant={clip.video_status === 'complete' ? 'success-muted' : 'warning-muted'}
          size="xs"
        >
          Video: {clip.video_status}
        </Badge>
      )}
    </div>
  );
}

const GenerationProgress: React.FC<GenerationProgressProps> = ({ generation, onDismiss, onCancel }) => {
  if (!generation) return null;

  const { clips, status } = generation;
  const isComplete = status === 'complete';
  const isError = status === 'error';
  const isDone = isComplete || isError;
  const isInFlight = !isDone;

  return (
    <div className="fixed bottom-6 right-6 z-[100] w-[380px] bg-slate-950/95 border border-slate-700 rounded-xl shadow-2xl shadow-black/50 backdrop-blur-md animate-in slide-in-from-bottom-4 duration-300">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
        <div className="flex items-center gap-2">
          {isDone ? (
            isComplete ? (
              <CheckCircle2 size={16} className="text-emerald-400" />
            ) : (
              <AlertCircle size={16} className="text-red-400" />
            )
          ) : (
            <Loader2 size={16} className="text-amber-400 animate-spin" />
          )}
          <span className={`text-xs font-bold uppercase tracking-widest ${statusColors[status] || 'text-slate-400'}`}>
            {isComplete ? 'Complete' : isError ? 'Error' : status === 'streaming' ? 'Streaming...' : 'Generating...'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {isInFlight && onCancel && (
            <button
              onClick={onCancel}
              className="flex items-center gap-1 px-2 py-1 text-xs text-red-400 hover:text-red-300 hover:bg-red-950/40 transition-colors rounded"
              title="Cancel generation"
            >
              <XCircle size={12} />
              Cancel
            </button>
          )}
          <button
            onClick={onDismiss}
            className="p-1 text-slate-500 hover:text-slate-300 transition-colors rounded"
            title="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Clip cards */}
      <div className="p-3 flex gap-3">
        {clips.length > 0 ? (
          clips.map((clip, i) => (
            <ClipCard key={clip.id} clip={clip} label={`Variant ${String.fromCharCode(65 + i)}`} />
          ))
        ) : (
          <div className="flex-1 text-center py-6 text-slate-500 text-xs">
            Waiting for clips...
          </div>
        )}
      </div>
    </div>
  );
};

export default GenerationProgress;
